import React, { useState } from 'react'
import {
  RefreshCw,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Download,
  Calendar,
  BarChart3,
  Activity,
  Search,
} from 'lucide-react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts'

import { Card, CardHeader, CardContent } from '../components/ui/Card'
import Button from '../components/ui/Button'
import Input from '../components/ui/Input'
import { StatusBadge } from '../components/ui/Badge'
import { Select } from '../components/ui/Input'
import { timeAgo, formatNumber, formatDate } from '../utils/cn'

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('auth_token') || ''}`,
})

const normalizeStatus = (status: string) => {
  if (status === 'success') return 'success'
  if (['partial', 'warning', 'dry_run'].includes(status)) return 'warning'
  if (['failed', 'error'].includes(status)) return 'error'
  return status || 'unknown'
}

const logTime = (log: any) => log.started_at || log.created_at || log.completed_at

const buildTrendData = (logs: any[]) => {
  const now = new Date()
  const buckets: Record<string, any> = {}

  for (let i = 23; i >= 0; i -= 1) {
    const date = new Date(now)
    date.setHours(now.getHours() - i, 0, 0, 0)
    const hour = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    buckets[hour] = { hour, success: 0, warning: 0, error: 0 }
  }

  logs.forEach((log) => {
    const rawTime = logTime(log)
    if (!rawTime) return
    const date = new Date(rawTime)
    if (Number.isNaN(date.getTime())) return
    if (now.getTime() - date.getTime() > 24 * 60 * 60 * 1000) return
    date.setMinutes(0, 0, 0)
    const hour = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    if (!buckets[hour]) return
    const status = normalizeStatus(log.status)
    if (status === 'success') buckets[hour].success += 1
    else if (status === 'warning') buckets[hour].warning += 1
    else buckets[hour].error += 1
  })

  return Object.values(buckets)
}

const buildProviderPerformance = (logs: any[]) => {
  const grouped = new Map<string, { provider: string; total: number; synced: number; nodes: number }>()
  logs.forEach((log) => {
    const provider = log.provider || 'Unknown'
    const current = grouped.get(provider) || { provider, total: 0, synced: 0, nodes: 0 }
    current.total += Number(log.total_nodes || 0)
    current.synced += Number(log.nodes_synced || 0)
    current.nodes = Math.max(current.nodes, Number(log.total_nodes || 0))
    grouped.set(provider, current)
  })

  return Array.from(grouped.values()).map((item) => ({
    provider: item.provider,
    success: item.total ? Math.round((item.synced / item.total) * 100) : 0,
    nodes: item.nodes,
  }))
}

const SyncLogs = () => {
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [providerFilter, setProviderFilter] = useState('all')
  const [dateRange, setDateRange] = useState('all')
  const [viewMode, setViewMode] = useState<'list' | 'chart'>('list')
  const queryClient = useQueryClient()

  const { data: logs = [], isLoading, error } = useQuery({
    queryKey: ['sync-logs'],
    queryFn: async () => {
      const response = await fetch('/api/v1/sync-logs/?limit=1000', {
        headers: authHeaders(),
      })
      if (!response.ok) throw new Error('Failed to fetch sync logs')
      return response.json()
    },
  })

  const filteredLogs = logs.filter((log: any) => {
    const provider = String(log.provider || 'Unknown')
    const errorText = String(log.error_message || '')
    const matchesSearch =
      provider.toLowerCase().includes(search.toLowerCase()) ||
      String(log.status || '').toLowerCase().includes(search.toLowerCase()) ||
      errorText.toLowerCase().includes(search.toLowerCase())

    const normalized = normalizeStatus(log.status)
    const matchesStatus = statusFilter === 'all' || normalized === statusFilter || log.status === statusFilter
    const matchesProvider = providerFilter === 'all' || String(log.provider_id) === providerFilter

    const rawTime = logTime(log)
    if (!rawTime) return matchesSearch && matchesStatus && matchesProvider && dateRange === 'all'
    const logDate = new Date(rawTime)
    const today = new Date()
    const matchesDate = () => {
      switch (dateRange) {
        case 'today':
          return logDate.toDateString() === today.toDateString()
        case 'yesterday': {
          const yesterday = new Date(today)
          yesterday.setDate(yesterday.getDate() - 1)
          return logDate.toDateString() === yesterday.toDateString()
        }
        case 'week': {
          const weekAgo = new Date(today)
          weekAgo.setDate(weekAgo.getDate() - 7)
          return logDate >= weekAgo
        }
        case 'month': {
          const monthAgo = new Date(today)
          monthAgo.setMonth(monthAgo.getMonth() - 1)
          return logDate >= monthAgo
        }
        default:
          return true
      }
    }

    return matchesSearch && matchesStatus && matchesProvider && matchesDate()
  })

  const totalNodes = logs.reduce((sum: number, log: any) => sum + Number(log.total_nodes || 0), 0)
  const syncedNodes = logs.reduce((sum: number, log: any) => sum + Number(log.nodes_synced || 0), 0)
  const totalDuration = logs.reduce((sum: number, log: any) => sum + Number(log.duration_ms || 0), 0)
  const stats = {
    total: logs.length,
    success: logs.filter((log: any) => normalizeStatus(log.status) === 'success').length,
    warning: logs.filter((log: any) => normalizeStatus(log.status) === 'warning').length,
    error: logs.filter((log: any) => normalizeStatus(log.status) === 'error').length,
    syncedNodes,
    successRate: totalNodes ? Math.round((syncedNodes / totalNodes) * 100) : 0,
    avgDuration: logs.length ? Math.round(totalDuration / logs.length) : 0,
  }

  const providerOptions = Array.from(
    new Map(logs.map((log: any) => [String(log.provider_id), log.provider || `Provider #${log.provider_id}`])).entries()
  ).filter(([id]) => id !== 'null' && id !== 'undefined')

  const handleRetrySync = async (log: any) => {
    if (!log.provider_id) {
      toast.error('This log is not linked to a provider')
      return
    }

    const response = await fetch(`/api/v1/providers/${log.provider_id}/sync`, {
      method: 'POST',
      headers: authHeaders(),
    })
    if (!response.ok) {
      toast.error('Retry failed')
      return
    }
    toast.success('Sync retried')
    queryClient.invalidateQueries({ queryKey: ['sync-logs'] })
  }

  const handleExportLogs = () => {
    const blob = new Blob([JSON.stringify(filteredLogs, null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `sync-logs-${new Date().toISOString().slice(0, 10)}.json`
    link.click()
    URL.revokeObjectURL(url)
  }

  const getStatusIcon = (status: string) => {
    switch (normalizeStatus(status)) {
      case 'success':
        return <CheckCircle className="w-5 h-5 text-success-500" />
      case 'warning':
        return <AlertCircle className="w-5 h-5 text-warning-500" />
      case 'error':
        return <XCircle className="w-5 h-5 text-error-500" />
      default:
        return <Activity className="w-5 h-5 text-secondary-500" />
    }
  }

  const formatDuration = (ms: number) => {
    const seconds = Math.floor(ms / 1000)
    if (seconds < 60) return `${seconds}s`
    const minutes = Math.floor(seconds / 60)
    const remainingSeconds = seconds % 60
    return `${minutes}m ${remainingSeconds}s`
  }

  if (isLoading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-10 rounded-lg bg-secondary-200 dark:bg-secondary-700" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-32 rounded-xl bg-secondary-200 dark:bg-secondary-700" />
          ))}
        </div>
        <div className="h-96 rounded-xl bg-secondary-200 dark:bg-secondary-700" />
      </div>
    )
  }

  if (error) {
    return (
      <Card>
        <div className="text-center py-12">
          <AlertCircle className="w-10 h-10 text-error-500 mx-auto mb-3" />
          <h2 className="text-lg font-semibold text-secondary-900 dark:text-white">Failed to load sync logs</h2>
          <Button className="mt-4" onClick={() => queryClient.invalidateQueries({ queryKey: ['sync-logs'] })}>
            Retry
          </Button>
        </div>
      </Card>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-secondary-900 dark:text-white">Sync Logs</h1>
          <p className="text-secondary-500 dark:text-secondary-400 mt-1">
            All values are loaded from recorded sync log rows.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="secondary"
            onClick={() => setViewMode(viewMode === 'list' ? 'chart' : 'list')}
            leftIcon={viewMode === 'list' ? <BarChart3 className="w-4 h-4" /> : <Calendar className="w-4 h-4" />}
          >
            {viewMode === 'list' ? 'Chart View' : 'List View'}
          </Button>
          <Button variant="primary" onClick={handleExportLogs} leftIcon={<Download className="w-4 h-4" />}>
            Export Logs
          </Button>
        </div>
      </div>

      <Card>
        <div className="flex flex-col md:flex-row md:items-center gap-4">
          <div className="flex-1">
            <Input
              placeholder="Search logs..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              leftIcon={<Search className="w-4 h-4" />}
              className="w-full"
            />
          </div>
          <div className="flex items-center gap-3">
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              options={[
                { value: 'all', label: 'All Status' },
                { value: 'success', label: 'Success' },
                { value: 'warning', label: 'Partial/Warning' },
                { value: 'error', label: 'Error' },
              ]}
              className="w-40"
            />
            <Select
              value={providerFilter}
              onChange={(e) => setProviderFilter(e.target.value)}
              options={[
                { value: 'all', label: 'All Providers' },
                ...providerOptions.map(([id, name]) => ({ value: id, label: String(name) })),
              ]}
              className="w-40"
            />
            <Select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
              options={[
                { value: 'all', label: 'All Time' },
                { value: 'today', label: 'Today' },
                { value: 'yesterday', label: 'Yesterday' },
                { value: 'week', label: 'Last 7 Days' },
                { value: 'month', label: 'Last 30 Days' },
              ]}
              className="w-40"
            />
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="text-center">
          <div className="p-4">
            <div className="text-2xl font-bold text-primary-600 dark:text-primary-400">{stats.total}</div>
            <div className="text-sm text-secondary-500 dark:text-secondary-400">Total Syncs</div>
          </div>
        </Card>
        <Card className="text-center">
          <div className="p-4">
            <div className="text-2xl font-bold text-success-600 dark:text-success-400">{stats.successRate}%</div>
            <div className="text-sm text-secondary-500 dark:text-secondary-400">Node Success Rate</div>
          </div>
        </Card>
        <Card className="text-center">
          <div className="p-4">
            <div className="text-2xl font-bold text-warning-600 dark:text-warning-400">{formatNumber(stats.syncedNodes)}</div>
            <div className="text-sm text-secondary-500 dark:text-secondary-400">Nodes Synced</div>
          </div>
        </Card>
        <Card className="text-center">
          <div className="p-4">
            <div className="text-2xl font-bold text-info-600 dark:text-info-400">{formatDuration(stats.avgDuration)}</div>
            <div className="text-sm text-secondary-500 dark:text-secondary-400">Avg Duration</div>
          </div>
        </Card>
      </div>

      {viewMode === 'chart' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <CardHeader title="Sync Activity (24h)" description="Sync operation count by recorded status" />
            <CardContent>
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={buildTrendData(logs)}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--secondary-200)" />
                    <XAxis dataKey="hour" stroke="var(--secondary-500)" fontSize={12} />
                    <YAxis stroke="var(--secondary-500)" fontSize={12} allowDecimals={false} />
                    <Tooltip />
                    <Area type="monotone" dataKey="success" stackId="1" stroke="#10b981" fill="#10b981" fillOpacity={0.2} name="Successful" />
                    <Area type="monotone" dataKey="warning" stackId="1" stroke="#f59e0b" fill="#f59e0b" fillOpacity={0.2} name="Partial/Warning" />
                    <Area type="monotone" dataKey="error" stackId="1" stroke="#ef4444" fill="#ef4444" fillOpacity={0.2} name="Errors" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader title="Provider Performance" description="Success rate calculated from sync logs" />
            <CardContent>
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={buildProviderPerformance(logs)}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--secondary-200)" />
                    <XAxis dataKey="provider" stroke="var(--secondary-500)" fontSize={12} angle={45} textAnchor="start" height={60} />
                    <YAxis stroke="var(--secondary-500)" fontSize={12} />
                    <Tooltip />
                    <Bar dataKey="success" fill="#3b82f6" name="Success Rate %" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {viewMode === 'list' && (
        <Card>
          <CardHeader title="Sync History" description={`Showing ${filteredLogs.length} of ${logs.length} sync operations`} />
          <CardContent>
            {filteredLogs.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 rounded-full bg-secondary-100 dark:bg-secondary-800 flex items-center justify-center mx-auto mb-4">
                  <RefreshCw className="w-8 h-8 text-secondary-500 dark:text-secondary-400" />
                </div>
                <h3 className="text-lg font-semibold text-secondary-900 dark:text-white mb-2">No sync logs found</h3>
                <p className="text-secondary-600 dark:text-secondary-400">
                  No real sync operation has matched the current filters.
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {filteredLogs.map((log: any) => (
                  <div key={log.id} className="p-4 rounded-lg border border-secondary-200 dark:border-secondary-700">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div className="flex items-start gap-4">
                        <div className="w-10 h-10 rounded-lg bg-secondary-100 dark:bg-secondary-800 flex items-center justify-center">
                          {getStatusIcon(log.status)}
                        </div>
                        <div>
                          <h4 className="font-medium text-secondary-900 dark:text-white">{log.provider || 'Unknown'}</h4>
                          <div className="flex flex-wrap items-center gap-2 mt-1">
                            <span className="text-sm text-secondary-500 dark:text-secondary-400">
                              Started: {logTime(log) ? formatDate(logTime(log), { hour: '2-digit', minute: '2-digit' }) : 'N/A'}
                            </span>
                            <span className="text-sm text-secondary-500 dark:text-secondary-400">|</span>
                            <span className="text-sm text-secondary-500 dark:text-secondary-400">
                              Duration: {formatDuration(Number(log.duration_ms || 0))}
                            </span>
                            <span className="text-sm text-secondary-500 dark:text-secondary-400">|</span>
                            <span className="text-sm text-secondary-500 dark:text-secondary-400">
                              V2Board: {log.metadata?.v2board_synced ?? 0} pushed
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="flex flex-col md:items-end gap-2">
                        <div className="flex items-center gap-4">
                          <div className="text-center">
                            <div className="text-2xl font-bold text-secondary-900 dark:text-white">{log.nodes_synced}</div>
                            <div className="text-xs text-secondary-500 dark:text-secondary-400">Nodes Synced</div>
                          </div>
                          {Number(log.nodes_failed || 0) > 0 && (
                            <div className="text-center">
                              <div className="text-2xl font-bold text-error-600 dark:text-error-400">{log.nodes_failed}</div>
                              <div className="text-xs text-secondary-500 dark:text-secondary-400">Failed</div>
                            </div>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <StatusBadge status={normalizeStatus(log.status) as any} size="sm" />
                          <Button variant="ghost" size="sm" onClick={() => handleRetrySync(log)} leftIcon={<RefreshCw className="w-3 h-3" />}>
                            Retry
                          </Button>
                        </div>
                      </div>
                    </div>

                    {log.error_message && (
                      <div className="mt-4 p-3 rounded-lg bg-error-50 dark:bg-error-900/30 border border-error-200 dark:border-error-700">
                        <div className="flex items-start gap-2">
                          <AlertCircle className="w-4 h-4 text-error-500 flex-shrink-0 mt-0.5" />
                          <div className="text-sm text-error-700 dark:text-error-400">{log.error_message}</div>
                        </div>
                      </div>
                    )}

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4 pt-4 border-t border-secondary-200 dark:border-secondary-700">
                      <div className="text-center">
                        <div className="text-sm font-medium text-secondary-900 dark:text-white">{log.metadata?.local_nodes_imported ?? 0}</div>
                        <div className="text-xs text-secondary-500 dark:text-secondary-400">Imported</div>
                      </div>
                      <div className="text-center">
                        <div className="text-sm font-medium text-secondary-900 dark:text-white">{log.metadata?.v2board_synced ?? 0}</div>
                        <div className="text-xs text-secondary-500 dark:text-secondary-400">Pushed</div>
                      </div>
                      <div className="text-center">
                        <div className="text-sm font-medium text-secondary-900 dark:text-white">{log.metadata?.unsupported_nodes ?? 0}</div>
                        <div className="text-xs text-secondary-500 dark:text-secondary-400">Unsupported</div>
                      </div>
                      <div className="text-center">
                        <div className="text-sm font-medium text-secondary-900 dark:text-white">
                          {Number(log.total_nodes || 0) ? Math.round((Number(log.nodes_synced || 0) / Number(log.total_nodes || 0)) * 100) : 0}%
                        </div>
                        <div className="text-xs text-secondary-500 dark:text-secondary-400">Success Rate</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  )
}

export default SyncLogs
